import { useMutation } from '@apollo/client';
import {
  EquipmentChecklistRoutineQuery,
  EquipmentChecklistRoutineQueryVariables,
  EquipmentInspectionAnswer,
  EquipmentInspectionAnswerType,
  EquipmentInspectionCondition,
  EquipmentInspectionsQuery,
  EquipmentInspectionsQueryVariables,
  EquipmentInspectionType,
  EquipmentInspectionUpdateInput,
} from '__generated__/graphql';
import { useAxios } from 'hooks';
import useGraphQLPaging from 'hooks/graphql/useGraphQLPaging/useGraphQLPaging';
import { first as firstItem, isNil, sortBy, startCase } from 'lodash';
import { DateTime } from 'luxon';
import ITimeRange from 'types/TimeRange';
import { getDueDateString, isoTimeStampUtc } from 'utils/dateUtils';
import { getFullNameFromMember } from 'utils/stringUtils';
import { DueWithin, IEquipmentInspectionsFilterData } from '../EquipmentInspectionsFilter/EquipmentInspectionsFilter';
import { IEquipmentInspectionCompletedTableData } from '../EquipmentInspectionsReportCompletedTable/EquipmentInspectionsReportTable';
import { IEquipmentInspectionUpcomingTableData } from '../EquipmentInspectionsReportUpcomingTable/EquipmentInspectionsReportUpcomingTable';
import { EQUIPMENT_CHECKLIST_ROUTINE_QUERY } from '../queries/equipment-inspection-checklist-routine-queries';
import {
  UPDATE_EQUIPMENT_CHECKLIST_ROUTINE,
  UPDATE_EQUIPMENT_INSPECTION,
} from '../queries/equipment-inspection-mutations';
import { EQUIPMENT_INSPECTION_QUERY } from '../queries/equipment-inspection-queries';
import useEquipmentChecklists from './useEquipmentChecklists';

export default function useEquipmentInspections(
  filterData?: IEquipmentInspectionsFilterData,
  timeRange?: ITimeRange<DateTime> | null
) {
  const pager = useGraphQLPaging();
  const { getEquipmentChecklists } = useEquipmentChecklists();
  const axios = useAxios();

  const [updateEquipmentInspectionMutation] = useMutation(UPDATE_EQUIPMENT_INSPECTION);
  const [updateEquipmentChecklistRoutineMutation] = useMutation(UPDATE_EQUIPMENT_CHECKLIST_ROUTINE);

  async function getCompletedEquipmentInspections(first?: number): Promise<IEquipmentInspectionCompletedTableData[]> {
    const result = await pager<EquipmentInspectionsQuery, EquipmentInspectionsQueryVariables>(
      {
        document: EQUIPMENT_INSPECTION_QUERY,
        variables: {
          first: first ?? 500,
          filter: {
            deletedOn: { isNull: true },
            inspectedOn: timeRange
              ? { between: { start: timeRange.startTime.toISO(), end: timeRange.endTime.toISO() } }
              : { isNull: false },
            equipmentId: filterData?.equipmentId ? { equal: filterData.equipmentId } : undefined,
            equipmentInspectionChecklistId: filterData?.checklistId ? { equal: filterData.checklistId } : undefined,
            condition: filterData?.condition
              ? { equal: filterData.condition as EquipmentInspectionCondition }
              : undefined,
            submittedBy: filterData?.inspectorId ? { equal: filterData.inspectorId } : undefined,
            type: filterData?.inspectionType
              ? { equal: filterData.inspectionType as EquipmentInspectionType }
              : undefined,
          },
        },
      },
      'equipmentInspections'
    );
    if (isNil(result)) {
      throw Error(`result was ${result}`);
    }

    const sortedResult = sortBy(result, (inspection) => inspection.submittedOn).reverse();
    return sortedResult.map((inspection) => {
      const issues = (inspection.answers as EquipmentInspectionAnswer[]).filter(
        (answer: EquipmentInspectionAnswer) => answer.answer === EquipmentInspectionAnswerType.Failed
      ).length;

      return {
        id: inspection.id,
        submitted: DateTime.fromISO(inspection.submittedOn, { zone: 'utc' }).toLocal().toFormat('EEE, LLL d'),
        equipment: inspection.equipment?.equipmentName ?? '',
        attachment: inspection.attachment?.equipmentName ?? '',
        inspectionType: startCase(inspection.type),
        issues,
        condition: inspection.condition!,
        resolved: inspection.resolvedOn ? true : false,
        checklist: inspection.checklist.name,
        inspector: inspection.submittedByMember
          ? getFullNameFromMember(inspection.submittedByMember)
          : getFullNameFromMember(inspection.checklist.inspector),
        hourMeter: inspection.equipment.lastHours?.runningHours,
        odometer: inspection.odometer,
        answers: inspection.answers as EquipmentInspectionAnswer[],
        signatureUrl: inspection.signatureUrl,
        resolveState: inspection.resolveState,
        resolveSignatureUrl: inspection.resolveSignatureUrl,
        resolveNotes: inspection.resolveNotes,
        resolvedBy: inspection.resolvedByMember ? getFullNameFromMember(inspection.resolvedByMember) : undefined,
        resolvedOn: inspection.resolvedOn
          ? DateTime.fromISO(inspection.resolvedOn, { zone: 'utc' }).toLocal().toFormat('EEE, LLL d')
          : undefined,
      };
    });
  }

  async function getUpcomingEquipmentInspections(first?: number): Promise<IEquipmentInspectionUpcomingTableData[]> {
    const result = await pager<EquipmentChecklistRoutineQuery, EquipmentChecklistRoutineQueryVariables>(
      {
        document: EQUIPMENT_CHECKLIST_ROUTINE_QUERY,
        variables: {
          first: first ?? 500,
          filter: {
            deletedOn: { isNull: true },
            equipmentId: filterData?.equipmentId ? { equal: filterData.equipmentId } : undefined,
            equipmentInspectionChecklistId: filterData?.checklistId ? { equal: filterData.checklistId } : undefined,
          },
        },
      },
      'equipmentChecklistRoutines'
    );
    if (isNil(result)) {
      throw Error(`result was ${result}`);
    }
    const results = await Promise.all(
      sortBy(result, (item) => item.createdOn)
        .reverse()
        .map(async (routine) => {
          const activeAssignments = (routine?.assignments ?? []).filter((a) => isNil(a.deletedOn));
          const assignment = firstItem(sortBy(activeAssignments, (a) => a.createdOn).reverse());
          const created = assignment
            ? DateTime.fromISO(assignment.createdOn, { zone: 'utc' }).toLocal()
            : DateTime.now();
          const effective = assignment
            ? DateTime.fromISO(assignment.effectiveOn, { zone: 'utc' }).toLocal()
            : DateTime.now();
          const laterDate = assignment
            ? created > effective
              ? created
              : effective
            : DateTime.fromISO(routine.createdOn, { zone: 'utc' }).toLocal();
          const dueDate = laterDate.plus({ days: routine?.recurringDays ?? 0 });
          const checklistQuery = await getEquipmentChecklists(1, {
            id: { equal: routine.equipmentInspectionChecklistId },
          });
          const checklist = firstItem(checklistQuery);
          return {
            id: routine.id,
            dueDateRaw: dueDate,
            dueDate: dueDate.toFormat('EEE, LLL d'),
            status: getDueDateString(dueDate),
            equipment: routine.equipment?.equipmentName ?? '',
            assignedTo: assignment?.member
              ? getFullNameFromMember(assignment?.member)
              : (checklist?.defaultInspector ?? ''),
            checklist: checklist?.checklistTitle ?? '',
            assignedToId: assignment ? assignment?.memberId : (checklist?.inspectorId ?? ''),
            assignmentIds: activeAssignments.map((a) => a.id),
            checklistId: checklist?.id ?? '',
          };
        })
    );
    let filteredResults = results;

    if (filterData?.inspectorId) {
      filteredResults = filteredResults.filter((item) => item.assignedToId === filterData.inspectorId);
    }

    const dueWithin = filterData?.dueWithin ?? DueWithin.THIRTY_DAYS;
    const now = DateTime.now();

    if (dueWithin === DueWithin.OVERDUE) {
      filteredResults = filteredResults.filter((item) => item.dueDateRaw < now);
    } else {
      const days = parseInt(dueWithin);
      const end = now.plus({ days });

      filteredResults = filteredResults.filter((item) => item.dueDateRaw >= now && item.dueDateRaw <= end);
    }

    return filteredResults;
  }

  async function updateEquipmentInspection(input: EquipmentInspectionUpdateInput) {
    const result = await updateEquipmentInspectionMutation({
      variables: {
        equipmentInspection: input,
      },
    });
    return result;
  }

  async function updateEquipmentInspectionResolveSignature(input: { id: string; signatureUrl: string }) {
    const decodedSignature = '<?xml version="1.0" encoding="UTF-8" standalone="no"?>\n' + input.signatureUrl;

    const params = new FormData();
    params.append('id', input.id);
    params.append('resolve_signature', new File([decodedSignature], 'signature.svg', { type: 'image/svg+xml' }));

    const result = await axios.patch('equipment-inspection', params);
    return result;
  }

  async function deleteEquipmentInspection(id: string) {
    const result = await updateEquipmentInspectionMutation({
      variables: {
        equipmentInspection: { id, deletedOn: isoTimeStampUtc() },
      },
    });
    return result;
  }

  async function deleteUpcomingEquipmentInspections(id: string) {
    const result = await updateEquipmentChecklistRoutineMutation({
      variables: {
        equipmentChecklistRoutine: { id, deletedOn: isoTimeStampUtc() },
      },
    });
    return result;
  }

  return {
    getCompletedEquipmentInspections,
    getUpcomingEquipmentInspections,
    updateEquipmentInspection,
    updateEquipmentInspectionResolveSignature,
    deleteEquipmentInspection,
    deleteUpcomingEquipmentInspections,
  };
}
